<template>
    <div class="flex flex-col min-h-screen text-sm text-[#4071a0] bg-[#e9e9e9]" style="overflow-x: hidden;">
        <!-- Notification System -->
        <NotificationList />

        <!-- Header -->
        <div class="fixed w-full bg-white outline-3 border-b z-999" data-testid="header">
            <div class="bg-white w-full flex items-center justify-between px-4 py-3 h-14">
                <div>
                    <img @click="router.goToHome()" src="~/assets/images/ad.svg" class="cursor-pointer max-w-150" data-testid="header-logo" />
                </div>
                <div class="flex items-center gap-3 flex-wrap">
                    <img class="h-7 filter invert-[0.4] brightness-[0.2] sepia-[0.3]"
                        src="~/assets/images/person.svg" />
                    <span class="hidden sm:inline" data-testid="header-user-email">{{ appState.userEmail.value }}</span>
                    <select v-model="selectedOrgId" @change="handleOrgChange" class="py-1 px-2 rounded border bg-white" data-testid="header-org-select">
                        <option v-for="org in organizations" :key="org.organizationId" :value="org.organizationId">
                            {{ org.organizationName }}
                        </option>
                    </select>

                    <button class="ml-2 cursor-pointer" @click="handleLogout" data-testid="header-logout-btn">{{ $t("home.logoutButton") }}</button>
                </div>
            </div>

            <div class="bg-[#eff2f5] w-full flex items-center justify-between px-4 py-3 h-14">
                <div class="flex items-center font-semibold text-2xl text-black">
                    Configuration Core

                    <button @click="toggleSidebar" class="pl-1 flex flex-col ml-3 mt-1 cursor-pointer" data-testid="sidebar-toggle-btn">
                        <div class="h-0.5 w-4 bg-[#4071a0] mb-1"></div>
                        <div class="h-0.5 w-4 bg-[#4071a0] mb-1"></div>
                        <div class="h-0.5 w-4 bg-[#4071a0]"></div>
                    </button>
                </div>
            </div>
        </div>

        <div class="flex flex-1 z-998">

            <!-- Mobile overlay mask -->
            <div v-if="isMobileView && isSidebarOpen" class="fixed inset-0 mt-28 bg-black opacity-20 z-30"
                @click="toggleSidebar">
            </div>

            <!-- Sidebar -->
            <aside :class="sidebarClasses" @mouseenter="onSidebarHover(true)" @mouseleave="onSidebarHover(false)" data-testid="sidebar">

                <nav class="space-y-1 text-white font-semibold overflow-y-auto mt-5 overflow-hidden whitespace-nowrap"
                    style="max-height: calc(100vh - 200px);">
                    <div class="mb-0 px-4 py-2 hover:bg-[#004280] cursor-pointer flex items-center"
                        @click="toggleAdminSubmenu" data-testid="sidebar-admin-menu">
                        <img src="~/assets/images/admin.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode || isFixedSidebar ? 'opacity-100 ml-2' : 'opacity-0'">Admin</span>
                        <span class="ml-auto">
                            <span v-if="showAdminSubmenu" class="chevron-down">
                                <svg class="w-3 h-3 dark:text-white" aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 8">
                                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                        stroke-width="2" d="m1 1 5.326 5.7a.909.909 0 0 0 1.348 0L13 1" />
                                </svg>
                            </span>
                            <span v-else class="chevron-right">
                                <svg class="w-3 h-3 dark:text-white" aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 8">
                                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                        stroke-width="2" d="M13 7 7.674 1.3a.91.91 0 0 0-1.348 0L1 7" />
                                </svg>
                            </span>
                        </span>
                    </div>

                    <div class="space-y-1 font-normal bg-[#4c87bf] overflow-hidden transition-all duration-300" :class="[
                        showAdminSubmenu ? 'max-h-60' : 'max-h-0'
                    ]">
                        <div @click="router.goToUserManagement()"
                            class="pl-8 px-2 py-1 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                            data-testid="sidebar-user-management">
                            <span class="sidebar-text transition-opacity duration-300"
                                :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("user.title") }}</span>
                        </div>
                        <div @click="router.goToRoleManagement()"
                            class="pl-8 px-2 py-1 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                            data-testid="sidebar-role-management">
                            <span class="sidebar-text transition-opacity duration-300"
                                :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("role.title") }}</span>
                        </div>
                        <div @click="router.goToPermissionManagement()"
                            class="pl-8 px-2 py-1 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                            data-testid="sidebar-permission-management">
                            <span class="sidebar-text transition-opacity duration-300"
                                :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("permission.title") }}</span>
                        </div>
                        <div @click="router.goToOrganizationManagement()"
                            class="pl-8 px-2 py-1 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                            data-testid="sidebar-organization-management">
                            <span class="sidebar-text transition-opacity duration-300"
                                :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("organization.title") }}</span>
                        </div>
                        <div v-if="hasPermission('PERM-000011')" @click="openHangfireDashboard"
                            class="pl-8 px-2 py-1 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                            data-testid="sidebar-hangfire-dashboard">
                            <span class="sidebar-text transition-opacity duration-300"
                                :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">Hangfire Dashboard</span>
                        </div>
                    </div>

                    <div @click="router.goToDashboard()"
                        class="px-4 py-2 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                        data-testid="sidebar-dashboard">
                        <img src="~/assets/images/history.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("side.dashboard") }}</span>
                    </div>
                    <div @click="router.goToBomList()"
                        class="px-4 py-2 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                        data-testid="sidebar-bom-list">
                        <img src="~/assets/images/config.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("side.bomList") }}</span>
                    </div>
                    <div @click="router.goToBomCreator()"
                        class="px-4 py-2 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                        data-testid="sidebar-bom-creator">
                        <img src="~/assets/images/add_config.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("side.bomCreator") }}</span>
                    </div>
                    <div @click="router.goToBomAlternativePart()"
                        class="px-4 py-2 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                        data-testid="sidebar-bom-alternative-part">
                        <img src="~/assets/images/alternative_part.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("side.alternativePart") }}</span>
                    </div>
                    <div @click="router.goToProductRuleList()"
                        class="px-4 py-2 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                        data-testid="sidebar-product-rule">
                        <img src="~/assets/images/product_rule.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("side.productRule") }}</span>
                    </div>
                    <div @click="router.goToBomCollectionView()"
                        class="px-4 py-2 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center"
                        data-testid="sidebar-bom-collections">
                        <img src="~/assets/images/upload.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">{{ $t("side.bomCollections") }}</span>
                    </div>
                    <!-- <div class="px-4 py-2 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center">
                        <img src="~/assets/images/history.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">編輯記錄查詢</span>
                    </div>
                    <div class="px-4 py-2 hover:bg-[#004280] hover:rounded cursor-pointer flex items-center">
                        <img src="~/assets/images/link.svg" class="min-w-[1.2rem] invert-[1]" />
                        <span class="sidebar-text transition-opacity duration-300"
                            :class="isMobileView || !isIconOnlyMode ? 'opacity-100 ml-2' : 'opacity-0'">其他連結</span>
                    </div> -->




                </nav>
            </aside>

            <div :class="['flex-1 flex flex-col w-full transition-all duration-500', isFixedSidebar ? 'lg:ml-0' : 'lg:ml-15']">

                <div class="flex-1 bg-[#e9e9e9] p-[100px_20px_70px_20px]">
                    <div :class="['flex pt-10']" data-testid="breadcrumb">
                        <p class="cursor-pointer" @click="router.goToHome()" data-testid="breadcrumb-home">{{ $t("home.home") }}</p>
                        <p class="px-2">/</p> <b data-testid="breadcrumb-current">{{ currentPageTitle }}</b>
                    </div>

                    <div :class="['flex-1 pt-4 font text-[26px] font-bold text-black']" data-testid="section-title">
                        {{ currentSection }}
                    </div>

                    <!-- Main Content -->
                    <main :class="['flex-1 p-6 lg:ml-0 mt-6 bg-white']">
                        <NuxtPage />
                    </main>
                </div>

                <!-- Footer -->
                <footer class="text-black p-4 text-left w-full lg:ml-15" data-testid="footer">
                    <p data-testid="footer-copyright">© 1983-2025 Advantech Co., Ltd.</p>
                    <div>
                        {{ $t("home.language") }} :
                        <select 
                            v-model="selectedLanguage" 
                            @change="changeLanguage"
                            class="py-1 px-2 rounded border bg-white ml-2"
                            data-testid="footer-language-select"
                        >
                            <option value="en">English</option>
                            <option value="zh">中文</option>
                            <option value="jp">日本語</option>
                            <option value="kr">한국어</option>
                        </select>
                    </div>
                </footer>
            </div>

        </div>

    </div>
</template>


<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useAppState } from "~/composables/useAppState";
import { useAuth } from "~/composables/auth/useAuth";
import { useRoutes } from "~/composables/useRoutes";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import NotificationList from '~/components/shared/NotificationList.vue';

const appState = useAppState();
const router = useRoutes();
const auth = useAuth();
const route = useRoute();
const { t, locale, setLocale } = useI18n();
const { hasPermission } = usePermission();
const config = useRuntimeConfig();
const openHangfireDashboard = () => {
    window.open(`${config.public.apiBase}/hangfire`, '_blank');
};

// Language switching functionality
const selectedLanguage = ref(appState.settings.value.language || locale.value);
const changeLanguage = async () => {
    try {
        // Update i18n locale using setLocale method
        const validLocales = ['en', 'zh', 'jp', 'kr'];
        if (validLocales.includes(selectedLanguage.value)) {
            await setLocale(selectedLanguage.value as 'en' | 'zh' | 'jp' | 'kr');
        }
        
        // Update app state to persist the language preference
        appState.updateSettings({ language: selectedLanguage.value });
    } catch (error) {
        console.error('Failed to change language:', error);
    }
};

watch(locale, (newLocale) => {
    if (newLocale !== selectedLanguage.value) {
        selectedLanguage.value = newLocale;
    }
});

watch(() => appState.settings.value.language, (newLang) => {
    if (newLang && newLang !== selectedLanguage.value) {
        selectedLanguage.value = newLang;
    }
});


const organizations = computed(() => appState.userOrgs.value);

const selectedOrgId = computed({
    get: () => appState.orgId.value,
    set: (value) => appState.setOrgId(value)
});

const handleOrgChange = () => {
    console.log('Organization changed to:', appState.orgId.value);
    window.location.reload();
};

const handleLogout = async () => {
    await auth.logout();
};

onMounted(() => {
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    // Initialize language from stored settings
    const storedLanguage = appState.settings.value.language;
    const validLocales = ['en', 'zh', 'jp', 'kr'];
    if (storedLanguage && storedLanguage !== locale.value && validLocales.includes(storedLanguage)) {
        setLocale(storedLanguage as 'en' | 'zh' | 'jp' | 'kr');
        selectedLanguage.value = storedLanguage;
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize);
});

const isMobileView = ref(false);
const checkScreenSize = () => {
    isMobileView.value = window.innerWidth < 1024;
};


// Sidebar state >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
const isSidebarOpen = ref(false);
const isSidebarHovered = ref(false);
const showAdminSubmenu = ref(false);
const isFixedSidebar = computed({
    get: () => appState.isFixedSidebar.value,
    set: (value) => appState.setIsFixedSidebar(value)
});

const toggleSidebar = () => {
    if (window.innerWidth < 1024) { // Mobile
        isSidebarOpen.value = !isSidebarOpen.value;
    } else { // Desktop
        isFixedSidebar.value = !isFixedSidebar.value;
        if (!isFixedSidebar.value) { // Reset submenu when switching to non-fixed
            showAdminSubmenu.value = false;
        }
    }
};

const toggleAdminSubmenu = (e: Event) => {
    e.stopPropagation();
    showAdminSubmenu.value = !showAdminSubmenu.value;
};

const isIconOnlyMode = computed(() => {
    return !isMobileView.value && !isFixedSidebar.value && !isSidebarHovered.value; // Desktop icon-only mode and not hovered
});

const sidebarClasses = computed(() => {
    const base = 'fixed mt-28 inset-y-0 left-0 z-999 bg-[#4071a0] transition-all duration-300';
    
    if (isMobileView.value) {
        return `${base} ${isSidebarOpen.value ? 'w-64' : 'w-0 -translate-x-full'}`;
    }
    
    // Desktop behavior
    const position = isFixedSidebar.value ? 'lg:relative' : 'lg:translate-x-0';
    const width = isFixedSidebar.value ? 'lg:w-64' : (isIconOnlyMode.value ? 'lg:w-16' : 'lg:w-64');
    const transform = (!isFixedSidebar.value && !isSidebarOpen.value) ? '-translate-x-full lg:translate-x-0' : '';
    
    return [base, position, width, transform].filter(Boolean).join(' ');
});

const onSidebarHover = (isHovering: boolean) => {
    if (!isMobileView.value && !isFixedSidebar.value) {
        isSidebarHovered.value = isHovering;

        if (!isHovering && showAdminSubmenu.value) {
            showAdminSubmenu.value = false; // Reset submenu
        }
    }
};



// Breadcrumb <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
const currentPageTitle = computed(() => {
    const path = route.path.toLocaleLowerCase();
    if (path.includes('/auth/user')) return t('side.admin');
    if (path.includes('/auth/role')) return t('side.admin');
    if (path.includes('/auth/permission')) return t('side.admin');
    if (path.includes('/auth/organization')) return t('side.admin');
    if (path.includes('/bom/bomlist')) return t('bom.title');
    if (path.includes('/productrule')) return t('productRule.title');
    if (path.includes('/bom/bomcreator')) { 
        if (path.includes('/edit')) {
            return t('bom.title');
        }
        return t('bomCreator.title'); 
    };
    if (path.includes('/bom/bomcollectionview')) return t('bomCollections.title');
    if (path.includes('/bom/bomcollectionedit')) return t('bomCollections.title');
    if (path.includes('/dashboard')) return t('side.dashboard');
    if (path.includes('/bom/bomalternativepart')) return t('alternativePart.title');
    return "";
});

const currentSection = computed(() => {
    const path = route.path.toLocaleLowerCase();
    if (path.includes('auth/user')) return t('user.title');
    if (path.includes('auth/role')) return t('role.title');
    if (path.includes('auth/permission')) return t('permission.title');
    if (path.includes('auth/organization')) return t('organization.title');

    if (path.includes('/bom/bomlist')) return t('side.bomList');
    if (path.includes('/productrule')){
        if (path.includes('/create')) {
            return t('productRule.createProductRuleTitle');
        } else if (path.includes('/copy')) {
            return t('productRule.copyProductRuleTitle');
        } else if (path.includes('/edit')) {
            return t('productRule.editProductRule');
        } else {
            return t('side.productRule');
        }
    }
    if (path.includes('/bom/bomcreator')) {
        if (path.includes('/edit')) {
            return t('bomCreator.editTitle');
        } else {
            return t('side.bomCreator');
        }
    }
    if (path.includes('/bom/bomcollectionview')) return t('side.bomCollections');
    if (path.includes('/bom/bomcollectionedit')) return t('side.bomCollections');
    if (path.includes('/bom/bomalternativepart')) return t('side.alternativePart');
    return "";
});

</script>